import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Spinner } from "@/shared/components/ui/spinner";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";

interface AsyncMultiSelectChipsProps<TOption> {
  value: number[];
  onChange: (ids: number[]) => void;
  searchFn: (term: string, signal: AbortSignal) => Promise<TOption[]>;
  loadByIds: (ids: number[], signal: AbortSignal) => Promise<TOption[]>;
  getOptionId: (option: TOption) => number;
  renderOption: (option: TOption) => ReactNode;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  inputId?: string;
  onSearchError?: (error: unknown) => void;
  onLoadByIdsError?: (error: unknown) => void;
}

const DEBOUNCE_MS = 300;

export function AsyncMultiSelectChips<TOption>({
  value,
  onChange,
  searchFn,
  loadByIds,
  getOptionId,
  renderOption,
  placeholder = "Buscar…",
  emptyText = "Nenhum resultado",
  disabled = false,
  inputId,
  onSearchError,
  onLoadByIdsError,
}: AsyncMultiSelectChipsProps<TOption>) {
  const [selectedItems, setSelectedItems] = useState<TOption[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<TOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const loadByIdsRef = useRef(loadByIds);
  const searchFnRef = useRef(searchFn);
  const onLoadByIdsErrorRef = useRef(onLoadByIdsError);

  useEffect(() => {
    loadByIdsRef.current = loadByIds;
    searchFnRef.current = searchFn;
    onLoadByIdsErrorRef.current = onLoadByIdsError;
  });

  useEffect(() => {
    const knownIds = new Set(selectedItems.map(getOptionId));
    const missing = value.filter((id) => !knownIds.has(id));

    if (missing.length === 0) {
      const valueSet = new Set(value);
      const trimmed = selectedItems.filter((item) =>
        valueSet.has(getOptionId(item)),
      );
      if (trimmed.length !== selectedItems.length) {
        // Sincronização com prop `value` externa: descarta chips cujo id
        // não está mais em value.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedItems(trimmed);
      }
      return;
    }

    const controller = new AbortController();
    loadByIdsRef
      .current(missing, controller.signal)
      .then((loaded) => {
        if (controller.signal.aborted) return;
        setSelectedItems((prev) => {
          const valueSet = new Set(value);
          const stillValid = prev.filter((item) =>
            valueSet.has(getOptionId(item)),
          );
          return [...stillValid, ...loaded];
        });
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        onLoadByIdsErrorRef.current?.(err);
      });

    return () => controller.abort();
  }, [value, selectedItems, getOptionId]);

  const searchFetcher = useCallback(
    (term: string, signal: AbortSignal) => searchFnRef.current(term, signal),
    [],
  );
  useDebouncedSearch({
    term: searchTerm,
    fetcher: searchFetcher,
    setItems: setSearchResults,
    setLoading: setSearching,
    onError: onSearchError,
    debounceMs: DEBOUNCE_MS,
  });

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleAdd = useCallback(
    (option: TOption) => {
      const id = getOptionId(option);
      if (value.includes(id)) return;
      setSelectedItems((prev) => [...prev, option]);
      onChange([...value, id]);
      setSearchTerm("");
      setSearchResults([]);
    },
    [value, onChange, getOptionId],
  );

  const handleRemove = useCallback(
    (id: number) => {
      setSelectedItems((prev) =>
        prev.filter((item) => getOptionId(item) !== id),
      );
      onChange(value.filter((current) => current !== id));
    },
    [value, onChange, getOptionId],
  );

  const visibleResults = searchResults.filter(
    (option) => !value.includes(getOptionId(option)),
  );

  return (
    <div ref={containerRef} className="relative">
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedItems.map((item) => {
            const id = getOptionId(item);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2 py-0.5 text-xs"
              >
                {renderOption(item)}
                <button
                  type="button"
                  onClick={() => handleRemove(id)}
                  disabled={disabled}
                  aria-label="Remover seleção"
                  className="rounded-full hover:bg-secondary-foreground/10 p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          id={inputId}
          value={searchTerm}
          placeholder={placeholder}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          className="pl-9"
        />
      </div>

      {open && searchTerm.trim().length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto z-50">
          {searching ? (
            <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
              <Spinner className="w-4 h-4" />
              Buscando…
            </div>
          ) : visibleResults.length === 0 ? (
            <div className="p-3 text-center text-sm text-muted-foreground">
              {emptyText}
            </div>
          ) : (
            visibleResults.map((option) => {
              const id = getOptionId(option);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleAdd(option)}
                  className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
                >
                  {renderOption(option)}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
